import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDashboardPath } from '../../config/roleConfig';
import { Eye, EyeOff } from 'lucide-react';
import { Logo } from '../common';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  const { login, isAuthenticated, user, error: authError, clearError, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (clearError) clearError();
    setLocalError('');
  }, [clearError]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const dashboardPath = getDashboardPath(user.role);
      navigate(dashboardPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (localError) setLocalError('');
    if (authError && clearError) clearError();
  }, [email, password]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!email.trim()) {
      setLocalError('Please enter your email address');
      return;
    }

    if (!password) {
      setLocalError('Please enter your password');
      return;
    }

    setIsSubmitting(true);
    setLocalError('');

    try {
      const result = await login(email, password);

      if (result.mfaRequired) {
        // Navigate to MFA challenge page
        navigate('/login/mfa', {
          state: { mfaToken: result.mfaToken, methods: result.methods },
          replace: true,
        });
        return;
      }

      if (result.success) {
        const pendingPlan = localStorage.getItem('pendingStripePlan');
        if (pendingPlan) {
          localStorage.removeItem('pendingStripePlan');
          window.location.href = '/pricing';
          return;
        }

        // Redirect to MFA setup if required
        if (result.mfaSetupRequired) {
          navigate('/mfa/setup', { replace: true });
          return;
        }

        const dashboardPath = getDashboardPath(result.user.role);
        navigate(dashboardPath, { replace: true });
        return;
      } else {
        setLocalError(result.error || 'Login failed. Please try again.');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setLocalError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const displayError = localError || authError;

  return (
    <div className="min-h-screen flex font-sans text-black antialiased bg-white">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#4D4D4D] relative overflow-hidden border-r border-[#4D4D4D]">
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <Logo variant="white" />
          </div>

          <div className="space-y-6 my-auto">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300">Registered investors & banking partners portal.</span>
            <h1 className="text-3xl lg:text-4xl font-bold font-sans text-white leading-tight">
              Inward fixed income investment, foreign exchange and forwards contract deal flow - access portal.
            </h1>
          </div>

          <div className="pt-6 border-t border-white/20 flex items-center justify-between text-xs font-mono text-zinc-300">
            <span>© 2026 Forwards Flow Inc.</span>
            <span>Registered investor and partner bank access only.</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Sign In Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12">
        <div className="max-w-md w-full">
          <div className="lg:hidden mb-8">
            <Logo />
          </div>

          <div className="mb-8">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">Portal Sign In</span>
            <h2 className="text-2xl font-bold font-sans text-black">Sign in to your account</h2>
            <p className="text-xs font-mono text-zinc-500 mt-1">Select account tier or enter authorized credentials</p>
          </div>

          <div className="border border-zinc-300 p-8 rounded-none bg-white">
            {displayError && (
              <div className="mb-6 p-4 bg-zinc-50 border border-zinc-300 rounded-none font-mono text-xs text-black">
                ⚠️ {displayError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-zinc-300 rounded-none text-xs font-mono text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors disabled:bg-zinc-100 disabled:cursor-not-allowed"
                  placeholder="you@example.com"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 pr-12 border border-zinc-300 rounded-none text-xs font-mono text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors disabled:bg-zinc-100 disabled:cursor-not-allowed"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-black focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember & Forgot */}
              <div className="flex items-center justify-between font-mono text-xs">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-[#4D4D4D] border-zinc-300 rounded-none focus:ring-0"
                  />
                  <span className="ml-2 text-zinc-600">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-zinc-700 hover:text-black font-medium underline uppercase tracking-wider">
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            {/* Demo Access Link */}
            <div className="mt-8 pt-6 border-t border-zinc-200 text-center">
              <span className="font-mono text-xs text-zinc-600">
                Want to see a demo?{' '}
                <Link to="/demo-access" className="text-black hover:underline font-bold uppercase tracking-wider">
                  Request demo access
                </Link>
              </span>
            </div>

            {/* Register Link */}
            <p className="mt-8 text-center text-xs font-mono text-zinc-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-black hover:underline font-bold uppercase tracking-wider">
                Request access
              </Link>
            </p>
          </div>

          <p className="mt-6 text-center text-xs font-mono text-zinc-400">
            Need help? Contact{' '}
            <a href="mailto:support@forwardsflow.com" className="text-zinc-700 hover:text-black underline">
              support@forwardsflow.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
